import {
    App, DatePicker, Row, Col, Typography, Input, Form, Button,
    InputNumber, List, Skeleton, Modal,
} from 'antd';
import {
  EyeOutlined
} from '@ant-design/icons';
import { useTranslation } from "react-i18next";
import { submitTravelRequest, getOpenTravelRequests } from "../../api/vehicle";
import { imageLocations } from "../image/ImageLocations";
import PanelFrameWithImage from "./PanelFrameWithImage";
import React, { useState, useEffect } from "react";
import {showApiError} from "../../utility/error/error-handler";
import {useScreenSizeBelow} from "../../utility/hooks/useScreenSize";
import dayjs from 'dayjs';
import useOnlineStatus from "../../utility/hooks/useOnlineStatus";
import {getRandomInt} from "../../utility/global-util";


const { Text} = Typography;
const { TextArea } = Input;

const SubmitTravelRequestPanel = ({vehicle, backgroundType, refreshPage, setRefreshPage}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);  //start 0 - button shows loading if less than 100
  const [submitProgress, setSubmitProgress] = useState(100);  //start 100 - button shows loading if less than 100
  const [openTravelRequests, setOpenTravelRequests] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const { t } = useTranslation();
  const { isOnline } = useOnlineStatus();
  const { message, notification } = App.useApp();
  const clickButton = (e) => {
    setIsModalVisible(true);
  };

  const viewRequest = (e, request) => {
    // console.log(request);
    setSelectedRequest(request);
  };

  useEffect(() => {
    if (vehicle)
    {
      getOpenTravelRequests(vehicle.vehicleId, setLoadingProgress)
      .then(res => {
          setOpenTravelRequests(res);}, )
      .catch(err => {
          showApiError(t('title.my_travel_requests'), err, notification);
        });
    }
    setRefreshPage(false);
  }, [t, notification, vehicle, refreshPage, setRefreshPage]);

  const Buttons = () => {
    return (
      isOnline
      ?
      <Button type="primary" loading={loadingProgress !== 100 && !vehicle} onClick={(e) => clickButton(e)}>{t('title.new_travel_request')}</Button>
      :
      <></>
    );
  }

  const ViewPanel = (openRequests) => {
    const { t } = useTranslation();
    let displayHeight=130;
    if (useScreenSizeBelow(992)) {
      displayHeight = null;
    }
    return (
      (openRequests.openRequests && openRequests.openRequests.length > 0)
      ?
      <div style={{height:displayHeight, overflow:'auto'}}>
      <List>
        {openRequests.openRequests.map(request => (
          <List.Item key={getRandomInt(1, 1000)} >
              <Row style={{width:'100%'}} align="middle">
                <Col span={21}>
                  <Text>{t('message.travel_request_received', {0: request.travelDate, 1 : request.destination})}</Text>
                </Col>
                <Col span={3}>
                  <EyeOutlined style={{ marginRight: '10px', fontSize: '20px', float: 'right'}} onClick={(e) => viewRequest(e, request)}/>
                </Col>
              </Row>
          </List.Item>
        ))}
      </List>
      </div>
      :
      <div>
        <Row><Text>{t('message.no_open_requests')}</Text></Row>
      </div>
    )
  }
  
  const onFinish = (values) => {
    submitTravelRequest(vehicle.vehicleId, values, setSubmitProgress)
       .then(res =>{
          message.success(t('message.successfully_submitted', {0: t('field.travel_request')}));
          setIsModalVisible(false);
          setRefreshPage(true);
    })
    .catch(err => showApiError(t('title.new_travel_request'), err, notification));;
  }
  
  const EntryForm = () => {
    return (
      <Form layout="vertical" id="travelRequest"
            onFinish={onFinish}>
        <Form.Item name="travelDate" label={t('field.travel_date')}
          rules={[
            { required: true, message: t('error.no_value_named', {0: t('field.travel_date') }) },
          ]}>
          <DatePicker format={t('pattern.date_only').toUpperCase()} 
                      disabledDate={(current) => {
                        let customDate = dayjs().format("YYYY-MM-DD");
                        return current && current < dayjs(customDate, "YYYY-MM-DD");}}/>
        </Form.Item>
        <Form.Item name="days" label={t('field.number_of_days')}
          rules={[
               { required: true, message: t('error.no_value_named', {0: t('field.number_of_days')}) },
          ]}>
          <InputNumber
              controls={false}
              precision={0}
              type='number'
              pattern="[0-9]*"
              inputMode="numeric"
              min={1}
              // max={} no max in DB
              placeholder={t('action.enter_value')}
              style={{ width: '120px' }} />
        </Form.Item>
        <Form.Item name="destination" label={t('field.destination')}
          rules={[
            { required: true, message: t('error.no_value_named', {0: t('field.destination')}) },
          ]}>
          <Input
              allowClear={true}
              maxLength={50}
              placeholder={t('action.enter_value')} />
        </Form.Item>
        <Form.Item name="additionalInformation" label={t('field.additional_information')}>
          <TextArea
              placeholder={t('action.enter_value')}
              rows={2}
              maxLength={2000}
              showCount />
        </Form.Item>
      </Form>
    )
  }

  return (
    <PanelFrameWithImage title={t('title.my_travel_requests')}
                        imageLocation={imageLocations.TRAVEL_IMAGE}
                        backgroundType={backgroundType} height="260px"
                        buttons={<Buttons />}>
        {vehicle //basing on just main object and not having 'loadingProgress === 100' as jumpy at end of load when goes to 0
            ? // Show/Submit Travel Requests
              <div>
                <ViewPanel openRequests={openTravelRequests}/>
                <Modal className="std-portal-popup"
                    open={isModalVisible}
                    title={t('title.new_travel_request')}
                    onCancel={() => setIsModalVisible(false)}
                    closable={true}
                    maskClosable={false}
                    keyboard={false}
                    footer={[
                      <Button key="cancel" onClick={(e) => setIsModalVisible(false)}>
                          {t('action.cancel')}
                      </Button>,
                      <Button type="primary" form="travelRequest" key="submit" htmlType="submit" loading={submitProgress < 100}>
                            {t('action.submit')}
                      </Button>,
                    ]}>
                    <EntryForm/>
                </Modal>
                <Modal className="std-portal-popup"
                    open={selectedRequest}
                    title={t('title.travel_request')}
                    closable={true}
                    maskClosable={true}
                    keyboard={true}
                    footer={null}
                    onCancel={() => {setSelectedRequest(null)}}>
                    <Row>
                      <Col xs={24} sm={24} md={12} lg={6} xl={6}><Text className='dp-field-label'>{t('field.travel_date')}</Text></Col>
                      <Col xs={24} sm={24} md={12} lg={6} xl={6}><Text>{selectedRequest ? selectedRequest.travelDate : ""}</Text></Col>
                      <Col xs={24} sm={24} md={12} lg={6} xl={6}><Text className='dp-field-label'>{t('field.number_of_days')}</Text></Col>
                      <Col xs={24} sm={24} md={12} lg={6} xl={6}><Text>{selectedRequest ? selectedRequest.days : ""}</Text></Col>
                    </Row>
                    <Row>
                      <Col xs={24} sm={24} md={12} lg={6} xl={6}><Text className='dp-field-label'>{t('field.destination')}</Text></Col>
                      <Col xs={24} sm={24} md={12} lg={6} xl={6}><Text>{selectedRequest ? selectedRequest.destination : ""}</Text></Col>
                    </Row>
                    {selectedRequest && selectedRequest.additionalInformation
                    ?
                    <Row>
                      <Col xs={24} sm={24} md={6} lg={6} xl={6}><Text className='dp-field-label'>{t('field.additional_information')}</Text></Col>
                      <Col xs={24} sm={24} md={18} lg={18} xl={18}><Text>{selectedRequest ? selectedRequest.additionalInformation : ""}</Text></Col>
                    </Row>
                    :
                    <></>
                    }
                </Modal>
              </div>
            : // Loading
            <div>
                <Skeleton active title={false} paragraph={{ rows: 4 }} />
            </div>}
    </PanelFrameWithImage>
);
}

export default SubmitTravelRequestPanel;